// src/components/Admin/AuditTrailReports.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Skeleton,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Download,
  Description,
  CalendarMonth,
  Preview,
} from '@mui/icons-material';
import api from '../../services/api';

const EVENT_COLORS = {
  'User Login':      'default',
  'Study Created':   'primary',
  'Report Created':  'info',
  'Report Approved': 'success',
  'Message Sent':    'secondary',
  'Ticket Created':  'warning',
  'User Created':    'primary',
  'Facility Created':'success',
};

function AuditTrailReports() {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [preview, setPreview] = useState([]);
  const [summary, setSummary] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const fetchPreview = useCallback(async () => {
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const response = await api.get('/admin/audit-trail/preview', {
        params: { month: selectedMonth, year: selectedYear }
      });
      setPreview(response.data.preview || []);
      setSummary(response.data.summary || null);
    } catch (err) {
      setPreviewError('Could not load preview.');
    } finally {
      setPreviewLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    fetchPreview();
  }, [fetchPreview]);

  const handleDownload = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const response = await api.get('/admin/audit-trail', {
        params: { month: selectedMonth, year: selectedYear, format: 'pdf' },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-trail-${monthNames[selectedMonth - 1]}-${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess(`Audit trail for ${monthNames[selectedMonth - 1]} ${selectedYear} downloaded successfully!`);
    } catch (err) {
      console.error('Download audit trail error:', err);
      setError(err.response?.data?.error || 'Failed to generate audit trail report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Description sx={{ fontSize: 32 }} />
          Audit Trail Reports
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Generate and download monthly audit trail reports containing all system activities, user actions, and data changes.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Controls */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarMonth />
                Select Report Period
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Month</InputLabel>
                    <Select
                      value={selectedMonth}
                      label="Month"
                      onChange={(e) => setSelectedMonth(e.target.value)}
                    >
                      {monthNames.map((month, index) => (
                        <MenuItem key={index + 1} value={index + 1}>
                          {month}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Year</InputLabel>
                    <Select
                      value={selectedYear}
                      label="Year"
                      onChange={(e) => setSelectedYear(e.target.value)}
                    >
                      {years.map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Download />}
                  onClick={handleDownload}
                  disabled={loading}
                  sx={{
                    px: 4,
                    py: 1.5,
                    background: 'linear-gradient(135deg, #3E6AE1 0%, #34C3FF 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2E5AD1 0%, #24B3EF 100%)',
                    },
                  }}
                >
                  {loading ? 'Generating Report...' : 'Download PDF Report'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Report contents */}
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(62, 106, 225, 0.1) 0%, rgba(52, 195, 255, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(62, 106, 225, 0.05) 0%, rgba(52, 195, 255, 0.05) 100%)',
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(62, 106, 225, 0.3)' : 'rgba(62, 106, 225, 0.2)'}`,
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Report Contents
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The audit trail report includes:
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0 }}>
              {[
                'User login activities',
                'Study creation and modifications',
                'Report creation, approval, and edits',
                'Email/message sending history',
                'Support ticket activities',
                'User account management',
                'Facility management activities',
              ].map((item) => (
                <Typography key={item} component="li" variant="body2" sx={{ mb: 1 }}>
                  {item}
                </Typography>
              ))}
            </Box>

            {/* Summary counts */}
            {summary && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  {monthNames[selectedMonth - 1]} {selectedYear} — Summary
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                  {[
                    { label: 'Logins', value: summary.userLogins },
                    { label: 'Studies', value: summary.studiesCreated },
                    { label: 'Reports', value: summary.reportsCreated },
                    { label: 'Approved', value: summary.reportsApproved },
                    { label: 'Messages', value: summary.messagesSent },
                    { label: 'Tickets', value: summary.ticketsCreated },
                    { label: 'New Users', value: summary.usersCreated },
                    { label: 'New Facilities', value: summary.facilitiesCreated },
                  ].map(({ label, value }) => (
                    <Chip
                      key={label}
                      label={`${label}: ${value}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: 11 }}
                    />
                  ))}
                </Box>
              </>
            )}
          </Paper>
        </Grid>

        {/* Preview table */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Preview />
                  Preview — Last 10 Activities ({monthNames[selectedMonth - 1]} {selectedYear})
                </Typography>
                {previewLoading && <CircularProgress size={20} />}
              </Box>

              {previewError && (
                <Alert severity="warning" sx={{ mb: 2 }}>{previewError}</Alert>
              )}

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewLoading
                      ? Array.from({ length: 5 }).map((_, i) => (
                          <TableRow key={i}>
                            {Array.from({ length: 5 }).map((__, j) => (
                              <TableCell key={j}><Skeleton variant="text" /></TableCell>
                            ))}
                          </TableRow>
                        ))
                      : preview.length === 0
                        ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                              No activity recorded for {monthNames[selectedMonth - 1]} {selectedYear}.
                            </TableCell>
                          </TableRow>
                        )
                        : preview.map((event, i) => (
                          <TableRow key={i} hover>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell>
                              <Chip
                                label={event.type}
                                size="small"
                                color={EVENT_COLORS[event.type] || 'default'}
                                sx={{ fontSize: 11 }}
                              />
                            </TableCell>
                            <TableCell>{event.description}</TableCell>
                            <TableCell>{event.user}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: 12 }}>
                              {new Date(event.timestamp).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))
                    }
                  </TableBody>
                </Table>
              </TableContainer>

              {!previewLoading && preview.length > 0 && summary && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                  Showing 10 of {summary.totalEvents} total events. Download the PDF for the full report.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default AuditTrailReports;
